import Badge from "./Badge";
import Button from "./Button";
import ListData from "./ListData";
import { pickBadgeColor, normalizeTypeForUi } from "@utils/utils";

/*Clinic report practitioner sidebar: type badge, name, submitted marker, select + optional Quick Look.
 * Implements the list UI via ListData + renderItem so we reuse selection/hover/empty-state behavior from the shared list primitive.
 */
const LEAVE_LABELS = {
  vacation: "On Vacation",
  sick: "Sick Leave",
  personal: "Personal Day",
  unpaid: "Unpaid Leave",
};

export default function ListPractitioner({
  practitioners = [],
  selectedId,
  onSelect,
  reportedIds = [],
  onQuickLook,
  onVacationIds = new Set(),
  leaveTypeByPractitionerId = {},
}) {
  return (
    // Outer shell: border + clip. ListData handles divide-y between rows (listItemBaseClass avoids a second border on each row).
    <div className="rounded-xl border border-palette-gray-subtle overflow-hidden bg-white">
      <ListData
        items={practitioners}
        currentItemId={selectedId}
        listContainerClass="divide-y divide-palette-gray-subtle"
        listItemBaseClass="group flex items-start justify-between gap-3 px-4 py-3 transition-colors"
        listSelectedClass="bg-palette-indigo-soft"
        listUnselectedClass="bg-white hover:bg-palette-gray-soft"
        emptyMessage="No practitioners"
        // Selection is on the inner button, not ListData’s row onClick — row still gets selected styling from currentItemId.
        renderItem={(practitioner, selected) => {
          const hasReported = reportedIds.includes(practitioner.id);
          const isOnVacation = onVacationIds.has(practitioner.id);
          const leaveType = leaveTypeByPractitionerId[practitioner.id];
          const leaveLabel = LEAVE_LABELS[leaveType] ?? "On Vacation";

          return (
            <>
              <button
                type="button"
                onClick={() => onSelect?.(practitioner.id)}
                className="flex min-w-0 flex-1 items-center gap-3 justify-start text-left rounded-none bg-transparent border-0 p-0 min-h-0 font-medium text-palette-gray-heading hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-palette-indigo-label cursor-pointer"
              >
                {/* pickBadgeColor replaces a hardcoded DDS/HYG check — now handles all 16 types */}
                <Badge color={pickBadgeColor(normalizeTypeForUi(practitioner.practitioner_type))}>
                  {practitioner.practitioner_type_display}
                </Badge>

                <span className="min-w-0 truncate font-semibold text-palette-slate-primary">
                  {practitioner.name}
                </span>
                {hasReported && (
                  <span
                    className="ml-1 inline-flex items-center text-palette-emerald-badge text-lg font-bold"
                    title="Report submitted"
                  >
                    ✓
                  </span>
                )}
                {isOnVacation && (
                  <span
                    className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-palette-amber-muted text-palette-amber-heading"
                    title={leaveLabel}
                  >
                    {leaveLabel}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-2 shrink-0">
                {onQuickLook && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickLook(practitioner);
                    }}
                    className={[
                      "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                      "transition",
                      selected
                        ? "border-palette-indigo-border bg-white text-palette-indigo-heading hover:bg-palette-indigo-soft"
                        : "border-palette-gray-divider bg-white text-palette-slate-heading hover:bg-palette-gray-soft",
                      "md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100",
                    ].join(" ")}
                    title="Quick Look"
                  >
                    Quick Look
                  </Button>
                )}
              </div>
            </>
          );
        }}
      />
    </div>
  );
}
